import { type FragmentType, useFragment } from '@/gql/codegen';
import { graphql } from '@/gql/codegen/gql';
import type React from 'react';
import styles from './index.module.scss';

import { UserEnrollmentStatus } from '@/components/UserEnrollmentStatus';
import { DesignatedEnrollmentCoursesSettingSection } from '@/routes/_embaUserLayout/-components/DesignatedEnrollmentCoursesSettingSection';
import { EndedEnrollmentCourseCard } from '@/routes/_embaUserLayout/-components/EndedEnrollmentCourseCard';
import { ImportantNotificationBlock } from '@/routes/_embaUserLayout/-components/ImportantNotificationBlock';

export const ENDED_USER_ENROLLMENT = graphql(/* GraphQL */ `
  fragment EndedUserEnrollment on UserEnrollment {
    id
    startDateText
    endDateText
    scheduleStatus
    scheduleStatusText
    enrollmentCourses {
      id
    }
    nonDesignatedEnrollmentCourses {
      id
      ...EndedEnrollmentCourseCard
    }
    designatedEnrollmentCoursesSettings {
      id
      userEnrollmentCourses {
        id
        ...EndedEnrollmentCourseCard
      }
      ...DesignatedEnrollmentCoursesSettingSection
    }
  }
`);

export const IMPORTANT_NOTIFICATION = graphql(/* GraphQL */ `
  fragment EmbaImportantNotification on ImportantNotification {
    id
    description
    url
    publishAtFromText
  }
`);

export type EndedUserEnrollmentPageProps = {
  currentEmbaUserEnrollment: FragmentType<typeof ENDED_USER_ENROLLMENT>;
  importantNotification: FragmentType<typeof IMPORTANT_NOTIFICATION> | null;
};

export const EndedUserEnrollmentPage = ({
  currentEmbaUserEnrollment,
  importantNotification,
}: EndedUserEnrollmentPageProps) => {
  const embaUserEnrollmentCoursesData = useFragment(
    ENDED_USER_ENROLLMENT,
    currentEmbaUserEnrollment,
  );
  const importantNotificationData = useFragment(
    IMPORTANT_NOTIFICATION,
    importantNotification,
  );
  const hasNonDesignated =
    embaUserEnrollmentCoursesData.nonDesignatedEnrollmentCourses.length > 0;

  return (
    <div className={styles.embaUserEnrollmentCourses}>
      {importantNotificationData && (
        <ImportantNotificationBlock
          dateStr={importantNotificationData.publishAtFromText}
          description={importantNotificationData.description}
          url={importantNotificationData.url}
        />
      )}
      <UserEnrollmentStatus
        scheduleStatus={embaUserEnrollmentCoursesData.scheduleStatus}
        scheduleStatusText={embaUserEnrollmentCoursesData.scheduleStatusText}
        enrollmentCoursesLength={
          embaUserEnrollmentCoursesData.enrollmentCourses.length
        }
        startDateText={embaUserEnrollmentCoursesData.startDateText}
        endDateText={embaUserEnrollmentCoursesData.endDateText}
      />
      {embaUserEnrollmentCoursesData.designatedEnrollmentCoursesSettings.map(
        (setting) => (
          <DesignatedEnrollmentCoursesSettingSection
            key={setting.id}
            setting={setting}
          >
            {setting.userEnrollmentCourses.map((course) => (
              <EndedEnrollmentCourseCard key={course.id} course={course} />
            ))}
          </DesignatedEnrollmentCoursesSettingSection>
        ),
      )}
      {hasNonDesignated && (
        <ul className={styles.courseList}>
          {embaUserEnrollmentCoursesData.nonDesignatedEnrollmentCourses.map(
            (course) => (
              <EndedEnrollmentCourseCard key={course.id} course={course} />
            ),
          )}
        </ul>
      )}
    </div>
  );
};
